import React, { useState, useRef } from 'react';
import axios from 'axios';
import { UploadCloud, Download, FileText, Loader2, CheckCircle, AlertCircle } from 'lucide-react';

export default function ResumeWatermark() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null); // { url, filename }
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const processFile = async (file) => {
    if (!file) return;

    const allowed = ['.pdf', '.doc', '.docx'];
    const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    if (!allowed.includes(ext)) {
      setError('Only PDF, DOC or DOCX files are allowed.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('File size must be under 10 MB.');
      return;
    }

    setError('');
    setLoading(true);
    if (result?.url) URL.revokeObjectURL(result.url);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append('resume', file);

      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/tools/watermark-resume`,
        formData,
        { responseType: 'blob' }
      );

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const baseName = file.name.replace(/\.[^/.]+$/, '');
      const filename = `${baseName}_Teneo.pdf`;

      setResult({ url, filename });

      // Auto-download immediately
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      console.error(err);
      setError('Failed to watermark the resume. Please try again.');
    } finally {
      setLoading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    processFile(e.dataTransfer.files[0]);
  };

  return (
    <div style={{ maxWidth: 640, margin: '0 auto' }}>
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 28, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
        <h3 style={{ margin: 0, fontSize: 20 }}>Resume Watermark Tool</h3>
        <p style={{ color: '#6b7280', marginTop: 6 }}>
          Upload a resume and a <b>"Teneo"</b> watermark is added automatically. The watermarked file downloads right away.
        </p>

        <div
          onClick={() => !loading && inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          style={{
            marginTop: 20,
            border: `2px dashed ${dragOver ? '#2563eb' : '#cbd5e1'}`,
            background: dragOver ? '#eff6ff' : '#f8fafc',
            borderRadius: 12,
            padding: '40px 20px',
            textAlign: 'center',
            cursor: loading ? 'not-allowed' : 'pointer',
          }}
        >
          {loading ? (
            <>
              <Loader2 size={40} color="#2563eb" style={{ animation: 'spin 1s linear infinite' }} />
              <p style={{ marginTop: 10 }}>Adding watermark...</p>
            </>
          ) : (
            <>
              <UploadCloud size={40} color="#2563eb" />
              <p style={{ margin: '10px 0 4px', fontWeight: 600 }}>Click to upload or drag &amp; drop</p>
              <p style={{ margin: 0, color: '#6b7280', fontSize: 13 }}>PDF, DOC, DOCX (max 10 MB)</p>
            </>
          )}
          <input
            ref={inputRef}
            type="file"
            accept=".pdf,.doc,.docx"
            style={{ display: 'none' }}
            onChange={(e) => processFile(e.target.files[0])}
          />
        </div>

        {error && (
          <div style={{ marginTop: 16, padding: 12, background: '#fef2f2', color: '#b91c1c', borderRadius: 8, display: 'flex', gap: 8, alignItems: 'center' }}>
            <AlertCircle size={18} /> {error}
          </div>
        )}

        {result && (
          <div style={{ marginTop: 16, padding: 14, background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#047857', fontWeight: 600 }}>
              <CheckCircle size={18} /> Watermark added successfully
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 10, gap: 10, flexWrap: 'wrap' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14 }}>
                <FileText size={16} /> {result.filename}
              </span>
              <a
                href={result.url}
                download={result.filename}
                style={{ background: '#10b981', color: '#fff', padding: '8px 16px', borderRadius: 6, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600 }}
              >
                <Download size={16} /> Download again
              </a>
            </div>
          </div>
        )}
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}